<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <div class="enrollee-detail-shell sba-slide-in">
      <div class="card-panel enrollee-empty-note" v-if="error">
        <i class="material-icons">info_outline</i>
        <span>{{ errorText }}</span>
      </div>

      <div class="card-panel enrollee-overview-card z-depth-0" v-if="!error">
        <div class="enrollee-overview-head">
          <div class="enrollee-title-block">
            <span class="enrollee-status-icon">
              <i class="material-icons">person</i>
            </span>
            <div>
              <label class="active teal-text">Direct Enrollee Report</label>
              <h5>{{ report.enrollee.name }}</h5>
              <p>
                <span class="chip grey lighten-4">{{ report.enrollee.enrollee_number || "No ID" }}</span>
                <span class="chip teal lighten-5">{{ windowLabel }}</span>
                <small class="grey-text">{{ windowDateRange }}</small>
              </p>
            </div>
          </div>
          <button class="btn-flat close-detail-button" title="Close" @click.prevent="handleBack">
            <i class="material-icons left">close</i>Close
          </button>
        </div>

        <div class="enrollee-overview-body">
          <div class="overview-metrics">
            <div class="overview-metric utilization">
              <h6>{{ formatMoney(report.encounter.gross_sent ?? report.encounter.sent) }}</h6>
              <small>Gross Utilization</small>
            </div>
            <div class="overview-metric real">
              <h6>{{ formatMoney(report.encounter.real_approved) }}</h6>
              <small>Real Utilization</small>
            </div>
            <div class="overview-metric providers">
              <h6>{{ providerCount }}</h6>
              <small>Providers</small>
            </div>
            <div class="overview-metric encounters">
              <h6>{{ report.encounter.count }}</h6>
              <small>Encounters</small>
            </div>
          </div>

          <div class="direct-context-note">
            <i class="material-icons">info_outline</i>
            <span>No invoice or family history is attached to this direct enrollee.</span>
          </div>

          <button class="btn-small waves-effect waves-light cyan darken-3" @click.prevent="downloadEnrollee">
            <i class="material-icons left">download</i>Download
          </button>
        </div>
      </div>

      <div class="card-panel monthly-panel" v-if="!error">
        <h6>Monthly Utilization</h6>
        <div class="month-row" v-for="month in report.monthly_utilization" :key="month.month">
          <span>{{ month.month }}</span>
          <div class="month-bar">
            <span :style="`width: ${monthlyWidth(month.sent)}%`"></span>
          </div>
          <div class="right-align">
            <strong>{{ formatMoney(month.gross_sent ?? month.sent) }}</strong><br />
            <small>{{ formatMoney(month.real_approved) }} real</small>
          </div>
        </div>
        <p class="grey-text" v-if="!report.monthly_utilization.length">No encounters found in this one-year window.</p>
      </div>

      <div class="card-panel" v-if="!error">
        <h6>Provider Utilization</h6>
        <div class="provider-row" v-for="provider in report.provider_utilization" :key="provider.provider_id">
          <div>
            <strong>{{ provider.provider?.name || "Provider" }}</strong><br />
            <small>{{ provider.encounter_count }} encounters</small>
          </div>
          <div class="right-align">
            <strong>{{ formatMoney(provider.gross_sent ?? provider.sent) }}</strong><br />
            <small>{{ formatMoney(provider.real_approved) }} real</small>
          </div>
        </div>
        <p class="grey-text" v-if="!report.provider_utilization.length">No provider utilization found.</p>
      </div>

      <div class="card-panel" v-if="!error">
        <h6>Encounters</h6>
        <div class="encounter-filter-grid">
          <div>
            <div class="input-field encounter-search">
              <label class="active">Search</label>
              <input
                type="search"
                v-model="encounterSearch"
                placeholder="Search provider, diagnosis or status"
                @keyup.enter="handleEncounterSearch"
                @search="handleEncounterSearch"
              />
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="encounterProvider" @change="handleEncounterSearch">
                <option value="">All providers</option>
                <option :value="provider.provider_id" v-for="provider in report.provider_utilization" :key="provider.provider_id">
                  {{ provider.provider?.name || "Provider" }}
                </option>
              </select>
              <label>Provider</label>
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="encounterMonth" @change="handleEncounterSearch">
                <option value="">All months</option>
                <option :value="month.month" v-for="month in report.monthly_utilization" :key="month.month">
                  {{ month.month }}
                </option>
              </select>
              <label>Month</label>
            </div>
          </div>
          <div class="encounter-actions">
            <button class="btn-small waves-effect waves-light grey" :class="{ disabled: !encounterSearch && !encounterProvider && !encounterMonth }" @click.prevent="clearEncounterFilters">
              <i class="material-icons left">clear</i>Clear
            </button>
          </div>
        </div>

        <div class="collection">
          <div class="collection-item" v-for="encounter in pagedEncounters" :key="encounter.id">
            <div class="row m-0">
              <div class="col s12 m7">
                <strong>{{ encounter.provider?.name || "Provider" }}</strong><br />
                <small>{{ encounter.diagnosis || "No diagnosis" }}</small>
              </div>
              <div class="col s12 m5 right-align">
                <strong>{{ formatMoney(encounter.gross_sent ?? encounter.sent) }}</strong><br />
                <small>{{ formatDate(encounter.encounter_date) }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="encounter-pager" v-if="encounterPageTotal > 1">
          <button
            class="btn-flat encounter-page-button"
            :class="{ disabled: encounterPage <= 1 }"
            @click.prevent="changeEncounterPage(encounterPage - 1)"
          >
            <i class="material-icons left">chevron_left</i>Previous
          </button>
          <span>Page {{ encounterPage }} of {{ encounterPageTotal }}</span>
          <button
            class="btn-flat encounter-page-button"
            :class="{ disabled: encounterPage >= encounterPageTotal }"
            @click.prevent="changeEncounterPage(encounterPage + 1)"
          >
            Next<i class="material-icons right">chevron_right</i>
          </button>
        </div>
        <p class="grey-text" v-if="!pagedEncounters.length">No encounters match this filter.</p>
      </div>
    </div>
  </template>
</template>

<script>
import { computed, onMounted, onUpdated, ref } from "vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import M from "materialize-css";

export default {
  name: "EnrolleeDetailInsightComponent",
  components: { LoadingDiv },
  props: ["enrolleeId", "enrolleeNumber"],
  emits: ["reset"],
  setup(props, context) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const encounterSearch = ref("");
    const encounterProvider = ref("");
    const encounterMonth = ref("");
    const encounterPage = ref(1);
    const encounterPageSize = 10;

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const formatDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const buildEnrolleeInsightUrl = (page = encounterPage.value) => {
      const params = [
        `encounter_page=${page}`,
        `encounter_per_page=${encounterPageSize}`,
      ];

      if (encounterSearch.value) {
        params.push(`encounter_q=${encodeURIComponent(encounterSearch.value)}`);
      }

      if (encounterProvider.value) {
        params.push(`encounter_provider=${encounterProvider.value}`);
      }

      if (encounterMonth.value) {
        params.push(`encounter_month=${encounterMonth.value}`);
      }

      if (props.enrolleeId) {
        return `operator/insight/utilization/enrollee/${props.enrolleeId}?${params.join("&")}`;
      }

      return `operator/insight/utilization/enrollee-number?e=${encodeURIComponent(props.enrolleeNumber || "")}&${params.join("&")}`;
    };

    const getItemsFromApi = (page = encounterPage.value) => {
      (async () => {
        const { res, err } = await getItems(buildEnrolleeInsightUrl(page));

        if (res.value) {
          response.value = res.value.data;
          encounterPage.value = res.value.data.encounters?.current_page || 1;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(() => {
      getItemsFromApi();
    });

    const handleBack = () => {
      context.emit("reset", true);
    };

    const handleEncounterSearch = () => {
      encounterPage.value = 1;
      getItemsFromApi(1);
    };

    const clearEncounterFilters = () => {
      encounterSearch.value = "";
      encounterProvider.value = "";
      encounterMonth.value = "";
      encounterPage.value = 1;
      getItemsFromApi(1);
    };

    const changeEncounterPage = (page) => {
      if (page < 1 || page > encounterPageTotal.value) return;
      encounterPage.value = page;
      getItemsFromApi(page);
    };

    const monthlyWidth = (sent) => {
      const max = Math.max(...report.value.monthly_utilization.map((x) => Number(x.sent || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(sent || 0) / max) * 100);
    };

    const downloadEnrollee = () => {
      const data = [
        {
          Section: "Summary",
          Name: report.value.enrollee.name,
          Detail: report.value.enrollee.enrollee_number || "",
          "Window Start": report.value.window.start,
          "Window End": report.value.window.end,
          Provider: "",
          Date: "",
          Diagnosis: "",
          Encounters: report.value.encounter.count,
          "Gross Utilization": report.value.encounter.gross_sent ?? report.value.encounter.sent,
          "Real Utilization": report.value.encounter.real_approved,
        },
        {},
      ];

      report.value.monthly_utilization.forEach((month, index) => {
        data.push({
          Section: index === 0 ? "Monthly Utilization" : "",
          Name: month.month,
          Detail: "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          Encounters: month.encounter_count,
          "Gross Utilization": month.gross_sent ?? month.sent,
          "Real Utilization": month.real_approved,
        });
      });

      data.push({});

      report.value.provider_utilization.forEach((provider, index) => {
        data.push({
          Section: index === 0 ? "Provider Utilization" : "",
          Name: provider.provider?.name || "Provider",
          Detail: "",
          Provider: "",
          Date: "",
          Diagnosis: "",
          Encounters: provider.encounter_count,
          "Gross Utilization": provider.gross_sent ?? provider.sent,
          "Real Utilization": provider.real_approved,
        });
      });

      data.push({});

      pagedEncounters.value.forEach((encounter, index) => {
        data.push({
          Section: index === 0 ? "Encounters" : "",
          Name: encounter.name,
          Detail: encounter.enrollee_number || "",
          Provider: encounter.provider?.name || "",
          Date: encounter.encounter_date,
          Diagnosis: encounter.diagnosis || "",
          Encounters: "",
          "Gross Utilization": encounter.gross_sent ?? encounter.sent,
          "Real Utilization": encounter.real_approved,
        });
      });

      if (data.length) {
        downloadExcelFx(data, `${report.value.enrollee.name}_Direct_Utilization`);
      }
    };

    const report = computed(() => response.value || {
      enrollee: {},
      window: {},
      encounter: {
        sent: 0,
        gross_sent: 0,
        real_approved: 0,
      },
      monthly_utilization: [],
      provider_utilization: [],
      encounters: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: encounterPageSize,
        total: 0,
      },
    });

    const pagedEncounters = computed(() => report.value.encounters?.data || []);
    const encounterPageTotal = computed(() => report.value.encounters?.last_page || 1);
    const providerCount = computed(() => report.value.provider_utilization?.length || 0);
    const errorText = computed(() => {
      if (String(error.value || "").includes("404")) {
        return "No record found";
      }

      return error.value;
    });
    const windowLabel = computed(() => "Last 1 year");
    const windowDateRange = computed(() => {
      if (!report.value.window?.start || !report.value.window?.end) {
        return "";
      }

      return `${formatDate(report.value.window.start)} - ${formatDate(report.value.window.end)}`;
    });

    return {
      handleBack,
      handleEncounterSearch,
      clearEncounterFilters,
      changeEncounterPage,
      downloadEnrollee,
      formatMoney,
      formatDate,
      monthlyWidth,
      isReady,
      error,
      errorText,
      encounterSearch,
      encounterProvider,
      encounterMonth,
      encounterPage,
      encounterPageTotal,
      providerCount,
      report,
      pagedEncounters,
      windowLabel,
      windowDateRange,
    };
  },
};
</script>

<style scoped>
.m-0 {
  margin: 0;
}

.enrollee-detail-shell {
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 84px);
  overflow-y: auto;
  padding-right: 4px;
}

.enrollee-detail-shell > .card-panel {
  margin: 0;
  width: 100%;
}

.enrollee-empty-note {
  align-items: center;
  background: #f8fafb;
  color: #607d8b;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-height: 96px;
}

.enrollee-empty-note i {
  color: #00897b;
  font-size: 20px;
}

.card-panel {
  animation: insightCardIn 0.24s ease both;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: none;
  color: #263238;
  margin: 0;
  padding: 24px 28px;
}

.enrollee-overview-card {
  padding: 0;
}

.enrollee-overview-head {
  align-items: center;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 126px;
  padding: 26px 28px;
}

.enrollee-title-block {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
}

.enrollee-status-icon {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.enrollee-status-icon i {
  font-size: 24px;
}

.enrollee-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.enrollee-title-block p {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}

.enrollee-title-block .chip {
  margin: 0;
}

.close-detail-button {
  color: #455a64;
  flex-shrink: 0;
  font-weight: 600;
  text-transform: none;
}

.enrollee-overview-body {
  padding: 24px 28px;
}

.overview-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 12px;
}

.overview-metric {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-height: 96px;
  padding: 16px;
}

.overview-metric h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 0 0 7px;
  overflow-wrap: anywhere;
}

.overview-metric small {
  color: #607d8b;
}

.overview-metric.utilization {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.overview-metric.real {
  background: #f1f8e9;
  border-color: #c5e1a5;
}

.overview-metric.encounters {
  background: #fff8e1;
  border-color: #ffecb3;
}

.overview-metric.providers {
  background: #e0f2f1;
  border-color: #b2dfdb;
}

.overview-metric.context {
  background: #f8fafb;
  border-color: #d8e4e8;
}

.direct-context-note {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #d8e4e8;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  margin: 12px 0 16px;
  padding: 11px 12px;
}

.direct-context-note i {
  color: #00897b;
  font-size: 19px;
}

.month-row {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 72px 1fr auto;
  margin: 10px 0;
}

.month-bar {
  background: #eceff1;
  height: 8px;
}

.month-bar span {
  background: #00838f;
  display: block;
  height: 8px;
}

.provider-row {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
}

.encounter-filter-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(260px, 1.4fr) minmax(170px, 0.8fr) minmax(170px, 0.8fr) auto;
  margin-top: 12px;
}

.encounter-filter-grid .input-field {
  margin: 0;
}

.encounter-search {
  margin-top: 0;
}

.encounter-actions {
  text-align: right;
}

.encounter-filter-grid + .collection {
  margin-top: 18px;
}

.encounter-pager {
  align-items: center;
  border-top: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 14px;
}

.encounter-pager span {
  color: #607d8b;
  font-size: 0.88rem;
  font-weight: 600;
}

.encounter-page-button {
  color: #455a64;
  font-weight: 600;
  text-transform: none;
}

@media only screen and (max-width: 992px) {
  .overview-metrics,
  .encounter-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 600px) {
  .enrollee-overview-head {
    align-items: flex-start;
    flex-direction: column;
    padding: 22px;
  }

  .enrollee-overview-body,
  .card-panel {
    padding: 22px;
  }

  .overview-metrics,
  .encounter-filter-grid,
  .month-row {
    grid-template-columns: 1fr;
  }
}
</style>
